// src/components/Website/ApiKeyManager.js
import React, { useState } from 'react';
import { Card, Button, InputGroup, Form, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { websiteService } from '../../api/services/websites';
import { useToastStore } from '../UI/Toast';

const ApiKeyManager = ({ website }) => {
    const websiteId = website.id;
    const [showApiKey, setShowApiKey] = useState(false);
    const addToast = useToastStore(state => state.addToast);
    const queryClient = useQueryClient();

    // Načtení API klíče
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['websiteApiKey', websiteId],
        queryFn: () => websiteService.getApiKey(websiteId).then(res => res.data),
        retry: 1
    });

    // Generování nového API klíče
    const generateKeyMutation = useMutation({
        mutationFn: () => websiteService.generateApiKey(websiteId),
        onSuccess: () => {
            addToast('API klíč byl úspěšně vygenerován', 'success');
            queryClient.invalidateQueries({ queryKey: ['websiteApiKey', websiteId] });
        },
        onError: (err) => {
            addToast(`Chyba při generování API klíče: ${err.message}`, 'danger');
        }
    });

    // Reset API klíče
    const resetKeyMutation = useMutation({
        mutationFn: () => websiteService.resetApiKey(websiteId),
        onSuccess: () => {
            addToast('API klíč byl úspěšně resetován', 'success');
            queryClient.invalidateQueries({ queryKey: ['websiteApiKey', websiteId] });
        },
        onError: (err) => {
            addToast(`Chyba při resetování API klíče: ${err.message}`, 'danger');
        }
    });

    // Kopírování API klíče do schránky
    const copyToClipboard = () => {
        if (data?.api_key) {
            navigator.clipboard.writeText(data.api_key)
                .then(() => {
                    addToast('API klíč byl zkopírován do schránky', 'success');
                })
                .catch(() => {
                    addToast('Nepodařilo se zkopírovat API klíč', 'danger');
                });
        }
    };

    // Zobrazení modálního okna pro potvrzení resetování klíče
    const handleResetConfirm = () => {
        if (window.confirm('Opravdu chcete resetovat API klíč? Tato akce je nevratná a všechny aplikace používající tento klíč přestanou fungovat.')) {
            resetKeyMutation.mutate();
        }
    };

    const isLoaded = !isLoading && !isError && data;
    const hasApiKey = isLoaded && data.api_key;

    return (
        <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white">
                <h5 className="mb-0">API Klíč</h5>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Načítání API klíče...</span>
                        </Spinner>
                    </div>
                ) : isError ? (
                    <Alert variant="danger">
                        Nepodařilo se načíst API klíč. {error?.message}
                    </Alert>
                ) : (
                    <>
                        <p>
                            Pomocí API klíče můžete přistupovat k datům o tomto webu z vašich aplikací nebo služeb.
                            {!hasApiKey && " Vygenerujte si svůj první API klíč."}
                        </p>

                        {hasApiKey ? (
                            <div className="mb-4">
                                <div className="mb-3">
                                    <InputGroup>
                                        <Form.Control
                                            type={showApiKey ? "text" : "password"}
                                            value={data.api_key}
                                            readOnly
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setShowApiKey(!showApiKey)}
                                        >
                                            <i className={`fas fa-${showApiKey ? 'eye-slash' : 'eye'}`}></i>
                                        </Button>
                                        <Button
                                            variant="outline-primary"
                                            onClick={copyToClipboard}
                                        >
                                            <i className="fas fa-copy me-1"></i> Kopírovat
                                        </Button>
                                    </InputGroup>
                                    <small className="text-muted">
                                        Vygenerováno: {new Date(data.generatedAt).toLocaleString()}
                                    </small>
                                </div>

                                <div className="d-flex align-items-center">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Varování: Resetováním klíče zneplatníte všechny existující integrace.
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="outline-danger"
                                            onClick={handleResetConfirm}
                                            disabled={resetKeyMutation.isPending}
                                        >
                                            {resetKeyMutation.isPending ? (
                                                <>
                                                    <Spinner as="span" size="sm" animation="border" role="status" />
                                                    <span className="ms-2">Resetování...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-sync-alt me-2"></i> Resetovat klíč
                                                </>
                                            )}
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        ) : (
                            <div className="mb-4 text-center">
                                <p>Tento web zatím nemá vygenerovaný API klíč.</p>
                                <Button
                                    variant="primary"
                                    onClick={() => generateKeyMutation.mutate()}
                                    disabled={generateKeyMutation.isPending}
                                >
                                    {generateKeyMutation.isPending ? (
                                        <>
                                            <Spinner as="span" size="sm" animation="border" role="status" />
                                            <span className="ms-2">Generuji...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-key me-2"></i> Vygenerovat API klíč
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {hasApiKey && (
                            <div className="bg-light p-3 rounded mt-3">
                                <h6>Příklad použití API</h6>
                                <pre className="mb-0 language-bash" style={{ backgroundColor: 'transparent' }}>
                                    <code>
                                        {`curl -H "Authorization: Bearer ${data.apiKey}" \\\n     ${process.env.REACT_APP_API_URL || 'https://monitor.vilemis.cz/api'}/api/websites/${websiteId}/status`}
                                    </code>
                                </pre>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default ApiKeyManager;